import type { PostureStatus } from '@/components/ui/posture-chip';
import type { HeatColumn, HeatGroup, PostureColumn } from '@/components/ui/charts';
import type { HealthReadinessView, HrPostureCounts } from '@/lib/api/health-readiness';
import { formatDate, formatDayMonth } from '@/lib/utils/format';
import { localDayIso } from '../overview/overview-model';

/**
 * What the Snapshots & trend tab draws, read off the composite read
 * (T-0244 / E22 design §2; prototype `snapshotsTab()`, `postureColumns()`,
 * `heatmap()`). Pure, so the tab stays markup and these stay tested.
 *
 * Integrity rule (E22 design §6.3): every count, score, verdict and change
 * is the API's: the frozen posture counts and totals at each snapshot
 * (`trend[]`), each criterion's score at each snapshot (`criteria[].trend`,
 * null where a snapshot did not hold the criterion) and its change
 * (`trend_change`). What happens here is alignment (a criterion's scores
 * put under the snapshot columns by snapshot id) and the choice of a
 * "Today" column.
 */

/**
 * Whether the live state follows the snapshots as a "Today" column: there
 * is a snapshot, and none was captured today (prototype `live`).
 */
export function hasTodayColumn(view: HealthReadinessView, today: Date): boolean {
  const last = view.trend[view.trend.length - 1];
  if (!last) return false;
  return formatDate(last.capturedAt) !== formatDate(localDayIso(today));
}

function toCounts(c: HrPostureCounts): Record<PostureStatus, number> {
  return {
    healthy: c.healthy,
    'at-risk': c.atRisk,
    critical: c.critical,
    unassessed: c.unassessed,
  };
}

/** Domain ratings at each snapshot (the frozen counts), then today's posture counts. */
export function postureColumns(view: HealthReadinessView, today: boolean): PostureColumn[] {
  const cols: PostureColumn[] = view.trend.map((t) => ({
    label: formatDayMonth(t.capturedAt),
    counts: toCounts(t.posture.counts),
  }));
  if (today) {
    cols.push({ label: 'Today', live: true, counts: toCounts(view.verdict.posture.counts) });
  }
  return cols;
}

/**
 * A full column's height in domains: the most domains any column held (a
 * domain added or retired since an older snapshot changes the count).
 */
export function columnHeight(view: HealthReadinessView): number {
  return Math.max(
    view.verdict.posture.counts.total,
    ...view.trend.map((t) => t.posture.counts.total),
  );
}

/** The heat map's columns: one per snapshot, keyed by its id, then Today. */
export function heatColumns(view: HealthReadinessView, today: boolean): HeatColumn[] {
  const cols: HeatColumn[] = view.trend.map((t) => ({
    key: t.snapshotId,
    label: formatDayMonth(t.capturedAt),
  }));
  if (today) cols.push({ key: 'today', label: 'Today', live: true });
  return cols;
}

/**
 * Every criterion's score under each snapshot column, by category. A
 * criterion's trend point is found by its snapshot id (by its capture time
 * from an API that sends no id); a snapshot that did not hold the
 * criterion is null. Today's value is the live score; the change is the
 * API's `trend_change`.
 */
export function heatGroups(view: HealthReadinessView, today: boolean): HeatGroup[] {
  return view.criteriaGroups
    .filter((g) => g.criteria.length > 0)
    .map((g) => ({
      label: g.category,
      rows: g.criteria.map((c) => {
        const byId = new Map<string, number | null>();
        const byAt = new Map<string, number | null>();
        for (const p of c.trend) {
          if (p.snapshot_id) byId.set(p.snapshot_id, p.score_percent);
          else byAt.set(p.captured_at, p.score_percent);
        }
        const values = view.trend.map(
          (t) => byId.get(t.snapshotId) ?? byAt.get(t.capturedAt) ?? null,
        );
        if (today) values.push(c.scorePercent);
        return {
          id: c.id,
          name: c.name,
          weight: `${Math.round(c.weight * 100)}%`,
          values,
          change: c.trendChange,
        };
      }),
    }));
}
